import React from 'react'
import { logFrontendError } from '@/observability/frontendErrorLogger'

interface GlobalErrorBoundaryState {
  hasError: boolean
  error?: Error
  info?: React.ErrorInfo
}

export class GlobalErrorBoundary extends React.Component<
  React.PropsWithChildren,
  GlobalErrorBoundaryState
> {
  override state: GlobalErrorBoundaryState = { hasError: false }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    this.setState({ hasError: true, error, info })

    void logFrontendError({
      message: error.message,
      severity: 'react',
      stack: error.stack,
      metadata: { componentStack: info.componentStack },
    })
  }

  handleReload = (): void => {
    if (typeof window !== 'undefined') {
      window.location.reload()
    }
  }

  override render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children
    }

    const isProd =
      typeof process !== 'undefined' ? process.env?.NODE_ENV === 'production' : false

    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="text-2xl font-semibold">Something went wrong.</h1>
        <p className="text-sm text-muted-foreground">
          The interface hit an unexpected error. Please try reloading the page.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          className="rounded-xl border px-4 py-2 text-sm"
        >
          Reload
        </button>
        {!isProd && this.state.error && (
          <pre className="mt-4 max-h-64 w-full max-w-xl overflow-auto rounded-md border bg-muted p-2 text-left text-xs">
            {this.state.error.stack ?? this.state.error.message}
          </pre>
        )}
      </div>
    )
  }
}
